import {Link, NavLink, Outlet, useNavigate} from "react-router-dom";
import Footer from "../components/Footer";
import {useState} from "react";
import {Bars3Icon, XMarkIcon} from "@heroicons/react/16/solid";
import {Dialog, DialogPanel} from "@headlessui/react";
import NavDropdown from "../components/NavDropdown";

import kosmosLogo from "../assets/kosmos-logo-white.png";
import {useAuth} from "@/context/AuthContext.tsx";

const navClass = ({isActive}: { isActive: boolean }) =>
    `text-white text-lg transition ${isActive ? "text-primary font-bold" : "hover:text-primary"}`;

export default function PublicLayout() {
    const {user, logout} = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/", {replace: true});
    };

    const isAdmin = !!user?.roles?.includes("ADMIN");
    const isEditor = !!user?.roles?.includes("EDITOR");

    const main = [
        {to: "/nuus", label: "Jongste Nuus"},
        {to: "/nuus/kategorieë", label: "Kategorieë"},
        {to: "/nuus/luister", label: "Luister Nuus"},
        {to: "/nuus/op-die-kantlyn", label: "Op’ie Kantlyn"},
        {to: "/nuus/op-die-lug", label: "Op’ie Lug"},
        {to: "/nuus/die-dag-wat-was", label: "Die Dag Wat Was"},
        {to: "/nuus/ondersoek", label: "Nuus Ondersoek"},
    ];

    const onAirItems = [
        {to: "/op-die-lug/aanbieders", label: "Aanbieders"},
        {to: "/op-die-lug/programme", label: "Programme"},
    ];

    const podcastItems = [
        {to: "/potgooi/gesprekke", label: "Gesprekke"},
        {to: "/potgooi/gedagte-vir-die-dag", label: "Gedagte vir die Dag"},
    ];

    return (
        <div className="min-h-screen flex flex-col">
            <header className="bg-gray-800">
                <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
                    <Link to="/" className="-m-1.5 p-1.5 inline-flex items-center">
                        <span className="sr-only">Kosmos 94.1</span>
                        <img src={kosmosLogo} alt="Kosmos 94.1 Logo" className="h-10 w-auto"/>
                    </Link>

                    {/* Desktop nav */}
                    <div className="hidden lg:flex lg:items-center lg:gap-x-6">
                        <NavLink to="/" className={navClass} end>
                            Tuis
                        </NavLink>

                        <NavDropdown label="Nuus" items={main}/>

                        <NavLink to="/kompetisies" className={navClass}>
                            Kompetisies
                        </NavLink>

                        <NavDropdown label="Op Die Lug" items={onAirItems}/>

                        <NavDropdown label="Potgooi" items={podcastItems}/>

                        {user && (
                            <>
                                {isEditor && (
                                    <Link
                                        to="/editor/news"
                                        className="rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                                    >
                                        Editor
                                    </Link>
                                )}
                                {isAdmin && (
                                    <Link
                                        to="/admin/users"
                                        className="rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
                                    >
                                        Admin
                                    </Link>
                                )}
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="rounded-md bg-gray-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-gray-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:bg-gray-600"
                                >
                                    Logout
                                </button>
                            </>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="flex lg:hidden">
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(true)}
                            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white hover:bg-white/10"
                        >
                            <span className="sr-only">Open main menu</span>
                            <Bars3Icon aria-hidden="true" className="size-6"/>
                        </button>
                    </div>
                </nav>

                {/* Mobile menu */}
                <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                    <div className="fixed inset-0 z-50 bg-black/40" aria-hidden="true"/>
                    <DialogPanel
                        className="fixed bg-gray-800 inset-y-0 right-0 z-50 w-full overflow-y-auto p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="-m-1.5 p-1.5 inline-flex items-center"
                                  onClick={() => setMobileMenuOpen(false)}>
                                <span className="sr-only">Kosmos 94.1</span>
                                <img src={kosmosLogo} alt="Kosmos 94.1 Logo" className="h-10 w-auto"/>
                            </Link>
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                className="-m-2.5 rounded-md p-2.5 text-white hover:bg-white/10"
                            >
                                <span className="sr-only">Close menu</span>
                                <XMarkIcon aria-hidden="true" className="size-6"/>
                            </button>
                        </div>

                        <div className="mt-6 space-y-2">
                            <NavLink
                                to="/"
                                className={({isActive}) => `${navClass({isActive})} block py-2`}
                                onClick={() => setMobileMenuOpen(false)}
                                end
                            >
                                Tuis
                            </NavLink>

                            <p className="mt-4 text-sm uppercase tracking-wide text-white/60">Nuus</p>
                            {main.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({isActive}) => `${navClass({isActive})} block py-2`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    end
                                >
                                    {item.label}
                                </NavLink>
                            ))}

                            <NavLink
                                to="/kompetisies"
                                className={({isActive}) => `${navClass({isActive})} block py-2`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Kompetisies
                            </NavLink>

                            <p className="mt-6 text-sm uppercase tracking-wide text-white/60">Op Die Lug</p>
                            {onAirItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({isActive}) => `${navClass({isActive})} block py-2`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    end
                                >
                                    {item.label}
                                </NavLink>
                            ))}

                            <p className="mt-6 text-sm uppercase tracking-wide text-white/60">Potgooi</p>
                            {podcastItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({isActive}) => `${navClass({isActive})} block py-2`}
                                    onClick={() => setMobileMenuOpen(false)}
                                    end
                                >
                                    {item.label}
                                </NavLink>
                            ))}

                            {/* Role buttons on mobile */}
                            {user && (
                                <div className="pt-4 border-t border-white/10 space-y-3">
                                    {isEditor && (
                                        <Link
                                            to="/editor/news"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="block rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white text-center hover:bg-blue-500"
                                        >
                                            Editor
                                        </Link>
                                    )}
                                    {isAdmin && (
                                        <Link
                                            to="/admin/users"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="block rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white text-center hover:bg-emerald-500"
                                        >
                                            Admin
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="block w-full rounded-md bg-white/10 px-3.5 py-2 text-sm font-semibold text-white text-center hover:bg-white/20"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </DialogPanel>
                </Dialog>
            </header>

            <main className="flex-1">
                <Outlet/>
            </main>

            <Footer/>
        </div>
    );
}
